import { shade, tint } from 'polished';
import { BASIC_COLOURS, BASIC_NO_SWITCH_COLOURS, EXTRA_PALETTE_COLOURS, STATUS_COLOURS, StatusColour, TradingVariant } from './variants';
import { LinkStates, PaletteKey, StateColour } from './colourInterfaces';
import { getHexOpacity } from '../../common/utils';

const COLOURS = {
  // BASE
  WHITE: '#FFFFFF',
  BLACK: '#000000',

  // BASE_STATES
  WHITE_HOVER: '#E5E5E5',
  WHITE_ACTIVE: '#CCCCCC',

  BLACK_HOVER: '#1A1A1A',
  BLACK_ACTIVE: '#333333',

  // EXTRA BASE
  WHITE95: '#F2F2F2',

  // STATUS
  NEUTRAL: '#717171',
  INFO: '#2E90FA',
  SUCCESS: '#12B76A',
  WARNING: '#F79009',
  ERROR: '#F04438',

  // STATUS STATES (computed value is not matching with Figma)
  NEUTRAL_HOVER: '#515151',
  NEUTRAL_ACTIVE: '#484848',
  NEUTRAL_LIGHT: '#E2E2E2',
  NEUTRAL_LIGHT_HOVER: '#CBCBCB',
  NEUTRAL_LIGHT_ACTIVE: '#B5B5B5',

  INFO_HOVER: '#2068B4',
  INFO_ACTIVE: '#1D5CA0',
  INFO_LIGHT: '#D5E8FE',
  INFO_LIGHT_HOVER: '#C0D1E5',
  INFO_LIGHT_ACTIVE: '#AABACB',

  SUCCESS_HOVER: '#0D834C',
  SUCCESS_ACTIVE: '#0B7543',
  SUCCESS_LIGHT: '#CFF0E1',
  SUCCESS_LIGHT_HOVER: '#BAD8CA',
  SUCCESS_LIGHT_ACTIVE: '#A6C0B4',

  WARNING_HOVER: '#B16806',
  WARNING_ACTIVE: '#9E5C06',
  WARNING_LIGHT: '#FDE8CD',
  WARNING_LIGHT_HOVER: '#E4D1B8',
  WARNING_LIGHT_ACTIVE: '#CABAA4',

  ERROR_HOVER: '#AD3128',
  ERROR_ACTIVE: '#9A2B23',
  ERROR_LIGHT: '#FCD9D7',
  ERROR_LIGHT_HOVER: '#E3C3C1',
  ERROR_LIGHT_ACTIVE: '#CAAEAC',

  // HYPERLINKS
  LINK: '#006DCC',
  LINK_HOVER: '#0057A3',
  LINK_ACTIVE: '#004C8F',
  LINK_VISITED: '#006DCC',

  // HOT COLORS
  HOT_GREEN: '#28BE24',
  HOT_RED: '#F90404',
  HOT_BLUE: '#0457F9',
  HOT_ORANGE: '#F9A604',

  // TRADING COLOURS
  TRADING_NEGATIVE: '#EB0F29',
  TRADING_POSITIVE: '#00873C',
  TRADING_UNCHANGED: '#222',

  // MISC
  SHADOW: '#091E42',
  FOCUS: '#0000FF',
  YELLOW: '#FFCD00',

  // EXTRA COLOURS
  MINT: '#1AB99F',
  MINT_HOVER: '#17A68F',
  MINT_ACTIVE: '#15947F',
  ROSE: '#FF9090',
  ROSE_HOVER: '#E58282',
  ROSE_ACTIVE: '#CC7373',
  SKY: '#325FFF',
  SKY_HOVER: '#2D56E5',
  SKY_ACTIVE: '#284CCC'
} as const;

const BASE_COLOURS = {
  [BASIC_COLOURS.WHITE]: {
    colour: COLOURS.WHITE,
    hover: COLOURS.WHITE_HOVER,
    active: COLOURS.WHITE_ACTIVE,
    text: COLOURS.BLACK
  },
  [BASIC_COLOURS.BLACK]: {
    colour: COLOURS.BLACK,
    hover: COLOURS.BLACK_HOVER,
    active: COLOURS.BLACK_ACTIVE,
    text: COLOURS.WHITE
  },
  white95: COLOURS.WHITE95,
  [BASIC_NO_SWITCH_COLOURS.WHITE_NO_SWITCH]: {
    colour: COLOURS.WHITE,
    hover: COLOURS.WHITE_HOVER,
    active: COLOURS.WHITE_ACTIVE,
    text: COLOURS.BLACK
  },
  [BASIC_NO_SWITCH_COLOURS.BLACK_NO_SWITCH]: {
    colour: COLOURS.BLACK,
    hover: COLOURS.BLACK_HOVER,
    active: COLOURS.BLACK_ACTIVE,
    text: COLOURS.WHITE
  }
};

const EXTRA_COLOR_MULTIPLIERS = {
  5: 0.99,
  15: 0.95
};

const COLOR_MULTIPLIERS = {
  25: 0.905,
  50: 0.8,
  100: 0.707,
  200: 0.6,
  300: 0.4,
  400: 0.203,
  500: 0,
  600: 0.2,
  700: 0.4,
  800: 0.598,
  900: 0.698
};

const generateColourRamps = (colour: string, key: string, extraMultipliers = {}): Record<PaletteKey, 'string'> =>
  Object.assign(
    {},
    ...Object.entries({ ...extraMultipliers, ...COLOR_MULTIPLIERS }).map(([multiplier, amount]) => {
      let value = colour;
      const numberedMultiplier = Number.parseInt(multiplier, 10);
      if (numberedMultiplier > 500) {
        value = shade(amount, colour);
      } else if (numberedMultiplier < 500) {
        value = tint(amount, colour);
      }
      return { [`${key}${multiplier === '500' ? '' : multiplier}` as PaletteKey]: value.toUpperCase() };
    })
  );

export const PALETTE = {
  ...generateColourRamps(COLOURS.INFO, STATUS_COLOURS.INFO),
  ...generateColourRamps(COLOURS.SUCCESS, STATUS_COLOURS.SUCCESS),
  ...generateColourRamps(COLOURS.WARNING, STATUS_COLOURS.WARNING),
  ...generateColourRamps(COLOURS.ERROR, STATUS_COLOURS.ERROR),
  ...generateColourRamps(COLOURS.NEUTRAL, STATUS_COLOURS.NEUTRAL, EXTRA_COLOR_MULTIPLIERS),
  ...generateColourRamps(COLOURS.MINT, 'mint'),
  ...generateColourRamps(COLOURS.SKY, 'sky'),
  ...generateColourRamps(COLOURS.ROSE, 'rose')
};

const getVariations = (colour: string, isLight?: boolean) => ({
  colour: isLight ? COLOURS[`${colour.toUpperCase()}_LIGHT` as ColourKey] : PALETTE[`${colour}600` as PaletteKey],
  hover: COLOURS[`${colour.toUpperCase()}${isLight ? `_LIGHT` : ``}_HOVER` as ColourKey],
  active: COLOURS[`${colour.toUpperCase()}${isLight ? `_LIGHT` : ``}_ACTIVE` as ColourKey],
  text: isLight ? PALETTE[`${colour}800` as PaletteKey] : COLOURS.WHITE
});

type ColourKey = keyof typeof COLOURS;
const generateStatusStateColours = (status: StatusColour) =>
  ({
    [status]: {
      ...getVariations(status),
      light: {
        ...getVariations(status, true)
      }
    }
  }) as Record<StatusColour, StateColour>;

const STATUS: Record<StatusColour, StateColour> = {
  ...generateStatusStateColours(STATUS_COLOURS.INFO),
  ...generateStatusStateColours(STATUS_COLOURS.SUCCESS),
  ...generateStatusStateColours(STATUS_COLOURS.WARNING),
  ...generateStatusStateColours(STATUS_COLOURS.ERROR),
  ...generateStatusStateColours(STATUS_COLOURS.NEUTRAL)
};

const EXTRA_COLOURS = {
  [EXTRA_PALETTE_COLOURS.MINT]: getVariations(EXTRA_PALETTE_COLOURS.MINT),
  [EXTRA_PALETTE_COLOURS.SKY]: getVariations(EXTRA_PALETTE_COLOURS.SKY),
  [EXTRA_PALETTE_COLOURS.ROSE]: getVariations(EXTRA_PALETTE_COLOURS.ROSE)
};

const LINK_COLOURS: LinkStates = {
  colour: COLOURS.LINK,
  hover: COLOURS.LINK_HOVER,
  active: COLOURS.LINK_ACTIVE,
  visited: COLOURS.LINK_VISITED
};

const HOT_COLOURS = {
  green: COLOURS.HOT_GREEN,
  red: COLOURS.HOT_RED,
  blue: COLOURS.HOT_BLUE,
  orange: COLOURS.HOT_ORANGE
};

const TRADING_COLOURS: Record<TradingVariant, string> = {
  positive: COLOURS.TRADING_POSITIVE,
  negative: COLOURS.TRADING_NEGATIVE,
  unchanged: COLOURS.TRADING_UNCHANGED
};

export const MISC_COLOURS = {
  text: PALETTE.neutral900,
  background: PALETTE.neutral5,
  shadow: COLOURS.SHADOW,
  // focus: COLOURS.FOCUS,
  focus: 'transparent', // TODO: temporarily removed for SG release until focus ui is finalized
  yellow: COLOURS.YELLOW
};

// is overwritten by getBaseColours in eoraColourUtils
const TEXT_COLOURS = {
  dark: PALETTE.neutral900,
  default: PALETTE.neutral900,
  defaultNoSwitch: PALETTE.neutral900,
  light: PALETTE.neutral900,
  error: PALETTE.neutral900,
  disabled: PALETTE.neutral900,
  disabledNoSwitch: PALETTE.neutral900
};

const RAMP_ADDEND = 10;
const generateStates = (colour: string, text: string, startOpacity: number) => ({
  colour: `${colour}${getHexOpacity(startOpacity)}`,
  hover: `${colour}${getHexOpacity(startOpacity + RAMP_ADDEND)}`,
  active: `${colour}${getHexOpacity(startOpacity + RAMP_ADDEND * 2)}`,
  text
});

export const SECONDARY_COLOURS = {
  secondary: {
    ...generateStates(COLOURS.BLACK, COLOURS.WHITE, 60),
    light: {
      ...generateStates(COLOURS.BLACK, MISC_COLOURS.text, 10)
    }
  },
  secondaryInverted: {
    ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 60),
    light: {
      ...generateStates(COLOURS.WHITE, COLOURS.WHITE, 10)
    }
  }
};

const OPAQUE_COLOURS = {
  black: {
    ...generateStates(COLOURS.BLACK, COLOURS.WHITE, 70),
    light: {
      ...generateStates(COLOURS.BLACK, COLOURS.WHITE, 50)
    }
  },
  white: {
    ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70),
    light: {
      ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 50)
    }
  },
  // is overwritten by getBaseColours in eoraColourUtils
  black50: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  white50: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  black50NoSwitch: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  white50NoSwitch: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  black50Minimal: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  white50Minimal: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  black50NoSwitchMinimal: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) },
  white50NoSwitchMinimal: { ...generateStates(COLOURS.WHITE, COLOURS.BLACK, 70) }
};

export const baseColours = {
  ...SECONDARY_COLOURS,
  base: BASE_COLOURS,
  palette: PALETTE,
  status: STATUS,
  /**
   * @deprecated Use baseColours/coreColours/brandColours
   */
  links: LINK_COLOURS,
  /**
   * @deprecated Use baseColours/coreColours/brandColours
   */
  hot: HOT_COLOURS,
  trading: TRADING_COLOURS,
  misc: MISC_COLOURS,
  opaque: OPAQUE_COLOURS,
  /**
   * @deprecated Use baseColours/coreColours/brandColours
   */
  extra: EXTRA_COLOURS,
  text: TEXT_COLOURS
};

export type BaseColours = typeof baseColours;
